import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase_client';
import { useSession } from '../contexts/SessionContext';

export default function SetupProfilePage() {
  const navigate = useNavigate();
  const { user, profileMissing, refresh } = useSession();

  const [displayName, setDisplayName] = useState('');
  const [country, setCountry] = useState('MY');
  const [countryPhoneCode, setCountryPhoneCode] = useState('+60');
  const [phone, setPhone] = useState('');
  const [countries, setCountries] = useState<{ iso: string; name: string; country_phone_code: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // If user already has a profile, send them to the app
  useEffect(() => {
    if (!profileMissing && user) {
      navigate('/', { replace: true });
    }
  }, [profileMissing, user, navigate]);

  useEffect(() => {
    if (!user || !profileMissing) return;
    let cancelled = false;
    supabase.rpc('get_my_pending_invitation').then(({ data }) => {
      if (cancelled || !data?.success || !data.invitation_id) return;
      navigate(`/complete-profile?invitation=${data.invitation_id}`, { replace: true });
    });
    return () => {
      cancelled = true;
    };
  }, [user, profileMissing, navigate]);

  const fetchCountries = useCallback(async () => {
    const { data } = await supabase
      .from('countries')
      .select('iso, name, country_phone_code')
      .order('name', { ascending: true });
    if (data) setCountries(data);
  }, []);

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  // Keep phone code in sync with selected country
  useEffect(() => {
    const selected = countries.find(c => c.iso === country);
    if (selected?.country_phone_code) {
      setCountryPhoneCode(selected.country_phone_code);
    }
  }, [country, countries]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    if (!displayName.trim()) {
      setError('Display name is required');
      return;
    }
    if (!phone.trim()) {
      setError('Phone number is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error: insertError } = await supabase
        .from('user_profiles')
        .insert({
          id: user.id,
          display_name: displayName.trim(),
          contact_number: `${countryPhoneCode}${phone.trim()}`,
          country: country,
          locale: 'en',
        });

      if (insertError) {
        // If somehow already inserted (race condition), just continue
        if (insertError.code !== '23505') {
          throw insertError;
        }
      }

      // Refresh session so profileMissing clears and orgs load
      await refresh();
      navigate('/', { replace: true });
    } catch (err: any) {
      setError(err.message || 'Failed to save profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white shadow-lg rounded-xl w-full max-w-md p-8">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Complete Your Profile</h1>
          <p className="text-sm text-gray-500">
            We need a few details before you can continue.
          </p>
        </div>

        {/* Email (read-only) */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <input
            type="email"
            value={user?.email || ''}
            disabled
            className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 text-gray-500 text-sm"
          />
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Display Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              placeholder="e.g. John Doe"
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Country <span className="text-red-500">*</span>
            </label>
            <select
              value={country}
              onChange={e => setCountry(e.target.value)}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            >
              {countries.map(c => (
                <option key={c.iso} value={c.iso}>{c.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <div className="flex">
              <div className="relative">
                <select
                  value={countryPhoneCode}
                  onChange={e => setCountryPhoneCode(e.target.value)}
                  className="appearance-none border border-gray-300 rounded-l-lg bg-white text-gray-700 pl-3 pr-7 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none cursor-pointer"
                >
                  {countries.map(c => (
                    <option key={c.iso} value={c.country_phone_code}>
                      {c.iso} {c.country_phone_code}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
              <input
                type="tel"
                value={phone}
                onChange={e => setPhone(e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="123456789"
                required
                className="flex-1 border border-l-0 border-gray-300 rounded-r-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-medium py-2.5 rounded-lg transition-colors text-sm mt-2"
          >
            {loading ? 'Saving...' : 'Save & Continue'}
          </button>
        </form>

        <p className="text-center text-xs text-gray-400 mt-4">
          Signed in as {user?.email} —{' '}
          <button
            onClick={() => supabase.auth.signOut()}
            className="text-blue-500 hover:underline"
          >
            Sign out
          </button>
        </p>
      </div>
    </div>
  );
}
